import { useEffect, useState } from 'react'

import { getCitations } from './api'

/**
 * The citation registry, read once per document (V1-INTEGRATION step 5.5).
 *
 * Every Daily Status `evidence[slot].refs` is a list of IDs (`"[4]"`, `"[R11]"`)
 * and `GET /tracking/citations` is what they mean: the pinned eeg-tracking
 * release's `REFERENCES`, identical for every caller. PRODUCT.md §2: *"Every
 * claim shows its source. A reference the user sees must resolve to a real,
 * findable publication."*
 *
 * ── EVERY WORD IS THE ENDPOINT'S ────────────────────────────────────────────
 *
 * Authors, year, published title, venue and url are rendered as served — the
 * link's href AND its visible text are the entry's `url`. The client authors
 * none of them and corrects none of them — a typo fixed here would be a second
 * registry, drifting from the package one release later. `title` (the package's
 * internal "Author year — gloss" label) is NOT read at all: it is not the
 * work's title, and `published_title` is. Nor is `doi`: `url` already is the
 * work's link, and a client-built `https://doi.org/` one would not always be
 * the same page (an arXiv preprint's `url` is its abstract page).
 *
 * ── ONCE PER DOCUMENT, AND NEVER A GATE ─────────────────────────────────────
 *
 * The same module-level promise as `components/brain/loadCloud.ts`: the first
 * evidence panel to mount starts the read, every later one shares it, and a
 * REJECTION CLEARS IT so a later mount can try again. It is not user data — it
 * holds nobody's anything — so it survives a sign-out without leaking one
 * user's state to the next, which is what `useResource`'s no-cache rule exists
 * to prevent.
 *
 * No `signal` is passed: the read is shared, and one panel unmounting must not
 * abort it for the seven still on screen. `request()`'s own timeout bounds it.
 *
 * While it loads, or if it fails, `useCitations` returns `null` and the row
 * shows the raw IDs. A missing registry makes the References row less useful;
 * it must never take the evidence panel down with it.
 */

/** One resolvable reference: the endpoint's words, and the one link they name. */
export interface Citation {
  id: string
  authors: string
  year: number
  publishedTitle: string
  venue: string
  /**
   * The registry's own `url`, as served: `https://doi.org/<doi>` for a
   * published paper, the arXiv abstract page for a preprint. The client builds
   * no link from `doi` — a DOI-resolver URL assembled here would be a second
   * opinion on where the work lives, and for a preprint a different page from
   * the one the registry names. Always http(s): an entry whose `url` is not one
   * is not a findable publication, and is treated as unresolved.
   */
  url: string
}

export type CitationRegistry = ReadonlyMap<string, Citation>

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** A string with something in it. A blank field would render as a blank. */
const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null

/** The registry's `url`, if it is an http(s) link; anything else is no link at all. */
function webLink(url: string | null): string | null {
  if (url === null) return null
  try {
    const { protocol } = new URL(url)
    return protocol === 'https:' || protocol === 'http:' ? url : null
  } catch {
    return null
  }
}

function citationOf(entry: unknown): Citation | null {
  if (!isObject(entry)) return null
  const id = text(entry.id)
  const authors = text(entry.authors)
  const publishedTitle = text(entry.published_title)
  const venue = text(entry.venue)
  const year = typeof entry.year === 'number' && Number.isInteger(entry.year) ? entry.year : null
  const url = webLink(text(entry.url))
  if (id === null || authors === null || publishedTitle === null || venue === null) return null
  if (year === null || url === null) return null
  return { id, authors, year, publishedTitle, venue, url }
}

/**
 * The response, narrowed. A body without a `citations` array is a failure (raw
 * IDs everywhere); an ENTRY missing a field is skipped, so its ID alone renders
 * raw — never as a citation with a hole in it.
 */
export function readCitations(body: unknown): CitationRegistry {
  if (!isObject(body) || !Array.isArray(body.citations)) {
    throw new Error('citations: the response carries no citations list')
  }
  const registry = new Map<string, Citation>()
  for (const entry of body.citations) {
    const citation = citationOf(entry)
    if (citation !== null && !registry.has(citation.id)) registry.set(citation.id, citation)
  }
  return registry
}

let pending: Promise<CitationRegistry> | null = null
/** The settled registry, so a panel mounted after the read starts resolved. */
let settled: CitationRegistry | null = null

export function loadCitations(): Promise<CitationRegistry> {
  if (pending === null) {
    // Started inside a `.then`, so a throw while STARTING the read (a mocked
    // client without this export, say) is a rejection like any other rather
    // than an exception inside a component's effect.
    const attempt = Promise.resolve()
      .then(() => getCitations())
      .then(readCitations)
      .then((registry) => {
        settled = registry
        return registry
      })
    attempt.catch(() => {
      if (pending === attempt) pending = null
    })
    pending = attempt
  }
  return pending
}

/** The registry once it has arrived; `null` while it loads or after it failed. */
export function useCitations(): CitationRegistry | null {
  const [registry, setRegistry] = useState<CitationRegistry | null>(() => settled)

  useEffect(() => {
    let cancelled = false
    loadCitations().then(
      (value) => {
        if (!cancelled) setRegistry(value)
      },
      () => {
        // Nothing to do: the raw IDs are already on screen, and they stay.
      },
    )
    return () => {
      cancelled = true
    }
  }, [])

  return registry
}

/** Test seam: the promise is module state and would otherwise leak between cases. */
export function __resetCitationsForTests() {
  pending = null
  settled = null
}
